import { useState } from 'react';
import { useAppTheme, type Theme, type Density } from '../../contexts';

const THEMES: { value: Theme; label: string; icon: string }[] = [
  { value: 'dark',  label: 'Dark',  icon: '◑' },
  { value: 'light', label: 'Light', icon: '○' },
];
const DENSITIES: { value: Density; label: string }[] = [
  { value: 'spacious', label: 'Spacious' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'dense',    label: 'Dense'    },
];

export function TweaksPanel() {
  const [open, setOpen] = useState(false);
  const { theme, density, setTheme, setDensity } = useAppTheme();

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        title="Appearance settings"
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', right: 16, bottom: 16, zIndex: 90,
          width: 34, height: 34, borderRadius: 10,
          border: open ? '1px solid var(--accent-line)' : '1px solid var(--line)',
          background: open ? 'var(--accent-soft)' : 'var(--bg-1)',
          color: open ? 'var(--accent)' : 'var(--fg-3)',
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: 'var(--shadow-1)',
          transition: 'all .15s',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M12 2v2M12 20v2M2 12h2M20 12h2M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41"/>
        </svg>
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'fixed', right: 16, bottom: 58, zIndex: 90,
          width: 256,
          // Use a slightly more opaque/elevated bg so it stands out in both modes
          background: 'var(--bg-1)',
          border: '1px solid var(--line-strong)',
          borderRadius: 16,
          boxShadow: '0 8px 32px oklch(0% 0 0 / .35), 0 2px 8px oklch(0% 0 0 / .2)',
          overflow: 'hidden',
          backdropFilter: 'blur(12px)',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px 10px',
            borderBottom: '1px solid var(--line)',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-0)', letterSpacing: '-.01em' }}>Appearance</span>
            <button type="button" onClick={() => setOpen(false)} style={{
              all: 'unset', cursor: 'pointer', width: 22, height: 22, borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg-4)', fontSize: 14,
              transition: 'color .12s',
            }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--fg-1)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--fg-4)'}
            >✕</button>
          </div>

          <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Theme */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--fg-3)', marginBottom: 8 }}>Theme</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {THEMES.map(t => {
                  const active = t.value === theme;
                  return (
                    <button key={t.value} type="button" onClick={() => setTheme(t.value)} style={{
                      all: 'unset', flex: 1, cursor: 'pointer', textAlign: 'center',
                      padding: '9px 6px 8px',
                      borderRadius: 10,
                      border: active ? '1.5px solid var(--accent-line)' : '1.5px solid var(--line)',
                      background: active ? 'var(--accent-soft)' : 'var(--bg-2)',
                      color: active ? 'var(--accent)' : 'var(--fg-3)',
                      fontSize: 11, fontWeight: 600,
                      transition: 'all .12s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}
                      onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.borderColor = 'var(--line-strong)'; }}
                      onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.borderColor = 'var(--line)'; }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{t.icon}</span>
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Density */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--fg-3)', marginBottom: 8 }}>Density</div>
              <div style={{
                display: 'flex', padding: 3, borderRadius: 10,
                background: 'var(--bg-2)', border: '1px solid var(--line)',
              }}>
                {DENSITIES.map(d => {
                  const active = d.value === density;
                  return (
                    <button key={d.value} type="button" onClick={() => setDensity(d.value)} style={{
                      all: 'unset', flex: 1, cursor: 'pointer', textAlign: 'center',
                      padding: '5px 4px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                      color: active ? 'var(--accent)' : 'var(--fg-3)',
                      background: active ? 'var(--bg-0)' : 'transparent',
                      boxShadow: active ? 'var(--shadow-1)' : 'none',
                      border: active ? '1px solid var(--line)' : '1px solid transparent',
                      transition: 'all .12s',
                    }}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
